/**
 * 碰撞检测：圆-圆判定 + 均匀网格空间分区 + 线段-圆（激光）。
 * 纯 ESM，不依赖浏览器 API，node 可直接 import。
 */

/**
 * 两圆是否相交（边界相接算命中）。
 * @param {number} ax @param {number} ay @param {number} ar
 * @param {number} bx @param {number} by @param {number} br
 * @returns {boolean}
 */
export function circlesHit(ax, ay, ar, bx, by, br) {
  const dx = bx - ax;
  const dy = by - ay;
  const r = ar + br;
  return dx * dx + dy * dy <= r * r;
}

/**
 * 均匀网格空间分区，用于粗筛减少圆-圆检测次数。
 * 插入对象需有 {x, y, r} 字段。
 */
export class Grid {
  /**
   * @param {number} w 区域宽
   * @param {number} h 区域高
   * @param {number} cellSize 格子边长
   */
  constructor(w, h, cellSize) {
    this.w = w;
    this.h = h;
    this.cellSize = cellSize;
    this.cols = Math.max(1, Math.ceil(w / cellSize));
    this.rows = Math.max(1, Math.ceil(h / cellSize));
    /** @type {object[][]} */
    this.cells = new Array(this.cols * this.rows);
  }

  /** 清空所有格子。 */
  clear() {
    for (let i = 0; i < this.cells.length; i++) {
      this.cells[i] = undefined;
    }
  }

  /**
   * 按对象外接圆覆盖的所有格子插入。
   * 越界部分被裁剪到网格范围内。
   * @param {{x:number, y:number, r:number}} obj
   */
  insert(obj) {
    const cs = this.cellSize;
    let c0 = Math.floor((obj.x - obj.r) / cs);
    let c1 = Math.floor((obj.x + obj.r) / cs);
    let r0 = Math.floor((obj.y - obj.r) / cs);
    let r1 = Math.floor((obj.y + obj.r) / cs);
    if (c0 < 0) c0 = 0;
    if (r0 < 0) r0 = 0;
    if (c1 >= this.cols) c1 = this.cols - 1;
    if (r1 >= this.rows) r1 = this.rows - 1;
    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        const idx = row * this.cols + col;
        let cell = this.cells[idx];
        if (!cell) {
          cell = [];
          this.cells[idx] = cell;
        }
        cell.push(obj);
      }
    }
  }

  /**
   * 查询与给定圆可能碰撞的对象（粗筛，结果去重）。
   * @param {number} x @param {number} y @param {number} r
   * @param {object[]} outArray 结果写入此数组（先清空）
   * @returns {object[]} outArray
   */
  query(x, y, r, outArray) {
    outArray.length = 0;
    const cs = this.cellSize;
    let c0 = Math.floor((x - r) / cs);
    let c1 = Math.floor((x + r) / cs);
    let r0 = Math.floor((y - r) / cs);
    let r1 = Math.floor((y + r) / cs);
    if (c0 < 0) c0 = 0;
    if (r0 < 0) r0 = 0;
    if (c1 >= this.cols) c1 = this.cols - 1;
    if (r1 >= this.rows) r1 = this.rows - 1;
    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        const cell = this.cells[row * this.cols + col];
        if (!cell) continue;
        for (let i = 0; i < cell.length; i++) {
          const obj = cell[i];
          if (!obj._queried) {
            obj._queried = true;
            outArray.push(obj);
          }
        }
      }
    }
    for (let i = 0; i < outArray.length; i++) {
      outArray[i]._queried = false;
    }
    return outArray;
  }
}

/**
 * 线段 (x1,y1)-(x2,y2) 是否与圆 (cx,cy,cr) 相交（激光判定用）。
 * @returns {boolean}
 */
export function segmentHitsCircle(x1, y1, x2, y2, cx, cy, cr) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  let t = 0;
  if (lenSq > 0) {
    t = ((cx - x1) * dx + (cy - y1) * dy) / lenSq;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
  }
  const px = x1 + t * dx;
  const py = y1 + t * dy;
  const ddx = cx - px;
  const ddy = cy - py;
  return ddx * ddx + ddy * ddy <= cr * cr;
}
